import { type EnhancedStore, type Reducer, combineReducers, configureStore } from "@reduxjs/toolkit"

// Deep imports into the three slice modules, never the entity/page barrels. A barrel re-exports the
// entity's components and context too, so importing the reducer through one drags the whole entity
// and page graph into the store's own initialisation — and the store is imported by
// hooks/use-inject-reducer, which sits under the "@/3514/hooks" barrel that those same components
// import back. That is the top chokepoint in the dependency graph.
// Each slice module only reaches its own types and adapters, so none of them can close a cycle.
import {
    COACHING_CHATS_REDUCER_NAME,
    coachingChatReducer
} from "@/3514/entities/coaching-chat/store/coaching-chat.slice"
import {
    PARTICIPANT_HEADER_REDUCER_NAME,
    participantHeaderReducer
} from "@/3514/entities/participant.header/store/participant.header.slice"
import {
    COACH_DASHBOARD_REDUCER_NAME,
    coachDashboardReducer
} from "@/3514/pages/coach.dashboard/coach.dashboard.store.slice"

import { APP_REDUCER_NAME, appReducer } from "./app"
import { MODAL_REDUCER_NAME, modalReducer } from "./modal"

type _TStore = EnhancedStore & { dynamicReducers?: object }

const staticReducers: object = {
    [APP_REDUCER_NAME]: appReducer,
    [MODAL_REDUCER_NAME]: modalReducer,
    [PARTICIPANT_HEADER_REDUCER_NAME]: participantHeaderReducer,
    [COACHING_CHATS_REDUCER_NAME]: coachingChatReducer,
    [COACH_DASHBOARD_REDUCER_NAME]: coachDashboardReducer
}

let dynamicReducers: object = {}

function _createReducer(): Reducer {
    return combineReducers({
        ...staticReducers,
        ...dynamicReducers
    })
}

const store: _TStore = configureStore({
    reducer: _createReducer(),
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware({
            serializableCheck: false
        })
})

store.dynamicReducers = dynamicReducers

function injectReducer(key: string, reducer: Reducer): void {
    if (store.dynamicReducers[key]) return
    store.dynamicReducers[key] = reducer
    dynamicReducers = store.dynamicReducers
    store.replaceReducer(_createReducer())
}

function unloadReducer(key: string): void {
    if (!store.dynamicReducers[key]) return void 0

    delete store.dynamicReducers[key]

    return void 0
}

type IRootState = ReturnType<typeof store.getState>
type IAppDispatch = typeof store.dispatch

export type { IRootState, IAppDispatch }

export { store, injectReducer, unloadReducer }
